class CartDrawer extends HTMLElement {
  constructor() {
    super();

    if (this._boundOnce) return;
    this._boundOnce = true;

    this._onKeyUp = (evt) => evt.code === 'Escape' && this.close();
    this._onOverlayClick = this.close.bind(this);

    this.addEventListener('keyup', this._onKeyUp);
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this._onOverlayClick);
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;
    if (this._headerIconBound) return;
    this._headerIconBound = true;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    const wasAlreadyOpen = this.classList.contains('active');
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    requestAnimationFrame(() => {
      this.classList.add('animate', 'active');
    });

    // Self-heal: the progress headline can be stale from a prior render
    // (e.g. the Liquid all-unlocked branch used to render an empty <p>).
    // Reconcile now with the best cart total we have — cached if present,
    // otherwise fetched. Swallow any error so open() never breaks.
    if (typeof window.reconcileCartDrawerProgressHeadline === 'function') {
      if (typeof window.__lastCartTotalCents === 'number') {
        window.reconcileCartDrawerProgressHeadline(window.__lastCartTotalCents);
      } else if (typeof routes !== 'undefined' && routes.cart_url) {
        fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
          .then((r) => (r.ok ? r.json() : null))
          .then((cart) => {
            if (cart && typeof cart.total_price === 'number') {
              const q =
                typeof window.qualifyingSpendCentsFromCart === 'function'
                  ? window.qualifyingSpendCentsFromCart(cart)
                  : cart.total_price;
              window.__lastCartTotalCents = q;
              window.reconcileCartDrawerProgressHeadline(q);
            }
          })
          .catch((err) => { if (window.DEBUG_CART) console.warn('[cart] drawer-open: headline reconcile fetch failed', err); });
      }
    }

    let trapped = false;
    const trapFocusOnce = () => {
      if (trapped) return;
      trapped = true;
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    };

    this.addEventListener(
      'transitionend',
      () => {
        trapFocusOnce();
      },
      { once: true }
    );
    setTimeout(() => { trapFocusOnce(); }, 450);

    // Pin the page in place at its current scroll position. IMPORTANT:
    // renderContents() calls open() repeatedly while the drawer is already
    // active (gift/progress/upsell repaints). Re-capturing scroll in that
    // state can overwrite _savedScrollY with 0 (or a shifted value), which
    // later snaps the page to top on close. Only capture once per open cycle.
    if (!wasAlreadyOpen && !document.body.classList.contains('cart-drawer-open')) {
      this._savedScrollY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = `-${this._savedScrollY}px`;
      document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    } else {
      // Keep lock classes intact if we're already open.
      document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    }

    this._primeCheckoutPrefetch();
    this._bindCheckoutPrerenderOnIntent();
  }

  // Checkout URL honoring the current locale (e.g. /en/checkout).
  _checkoutUrl() {
    const cart = (window.routes && window.routes.cart_url) || '/cart';
    return cart.replace(/\/cart$/, '/checkout');
  }

  // Warm /checkout on first drawer open (low-priority prefetch). By the time
  // the shopper clicks Checkout, the HTML is usually in cache.
  _primeCheckoutPrefetch() {
    if (this._checkoutPrefetched) return;
    this._checkoutPrefetched = true;
    try {
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = this._checkoutUrl();
      link.as = 'document';
      document.head.appendChild(link);
    } catch (_) {}
  }

  // On shopper intent (hover/focus/touch Checkout) upgrade to prerender via
  // Speculation Rules. Chromium renders the page in a hidden tab so the
  // submit navigation is near-instant.
  _bindCheckoutPrerenderOnIntent() {
    // Bind on every open: renderContents swaps .drawer__inner innerHTML, so
    // the Checkout button is a NEW node after any cart update. The inner
    // `_checkoutPrerendered` flag still prevents firing the speculation rule
    // more than once per drawer instance.
    const btn = this.querySelector('#CartDrawer-Checkout, .cart__checkout-button');
    if (!btn || btn._prerenderIntentBound) return;
    btn._prerenderIntentBound = true;
    const fire = () => {
      if (this._checkoutPrerendered) return;
      this._checkoutPrerendered = true;
      try {
        if (!('supports' in HTMLScriptElement) || !HTMLScriptElement.supports('speculationrules')) return;
        const s = document.createElement('script');
        s.type = 'speculationrules';
        s.textContent = JSON.stringify({
          prerender: [{ source: 'list', urls: [this._checkoutUrl()] }],
        });
        document.head.appendChild(s);
      } catch (_) {}
    };
    const opts = { passive: true, once: true };
    btn.addEventListener('pointerenter', fire, opts);
    btn.addEventListener('touchstart', fire, opts);
    btn.addEventListener('focus', fire, opts);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    // Restore scroll lock set in open(): clear the inline top offset and
    // jump the window back to the saved Y so the shopper lands exactly
    // where they were before opening the drawer.
    document.body.style.top = '';
    if (typeof this._savedScrollY === 'number') {
      window.scrollTo(0, this._savedScrollY);
      this._savedScrollY = null;
    }
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    _lastDrawerUpdate = Date.now();
    _drawerVersion++;
    this.productId = parsedState.id;
    const prevProgress = captureProgressState();
    const liveProgressNode = captureLiveProgressNode();
    const liveUpsell = captureLiveUpsellNode();
    const drawerInner = document.querySelector('#CartDrawer .drawer__inner');
    if (drawerInner) drawerInner.classList.remove('gift-chooser-active');
    if (liveUpsell) liveUpsell.remove();

    /* Snapshot the live gift selector BEFORE we obliterate `.drawer__inner`
       innerHTML below. If the new server HTML is missing the selector for any
       reason (transient empty-cart edge cases, schema mismatches, etc.) we
       restore this clone so the banner never disappears mid-flow. The merchant
       has explicitly opted-in to "gift selector is permanent". */
    const giftSnapshot = (() => {
      const live = document.querySelector('#CartDrawer cart-drawer-gift-selector');
      return live ? live.cloneNode(true) : null;
    })();
    /* Auto-mode gift uses <cart-drawer-auto-gift> instead of the manual
       <cart-drawer-gift-selector>. Without a snapshot, an AJAX section
       render that loses overlay-group block context drops the element +
       its cart:updated listener, and subsequent adds (PDP, quick-add)
       no longer trigger gift reconcile until a hard reload. Capturing
       the live node lets us re-mount it after the swap if the new HTML
       came back without it. */
    const autoGiftSnapshot = (() => {
      const live = document.querySelector('#CartDrawer cart-drawer-auto-gift');
      return live ? live.cloneNode(true) : null;
    })();
    const giftConfigEnabled = document.getElementById('CartDrawer')?.getAttribute('data-cfg-gift') === 'true';

    this.getSectionsToRender().forEach((section) => {
      const raw = parsedState.sections[section.id];
      if (!raw) return;

      if (section.id === 'cart-drawer') {
        const doc = new DOMParser().parseFromString(raw, 'text/html');

        const srcDrawerDiv = doc.querySelector('#CartDrawer');
        const drawer = document.getElementById('CartDrawer');
        if (srcDrawerDiv && drawer) {
          ['data-cfg-hide-qty', 'data-cfg-upsell', 'data-cfg-progress', 'data-cfg-gift'].forEach((attr) => {
            const val = srcDrawerDiv.getAttribute(attr);
            if (val !== null) {
              // Overlay-group config flags are sticky: once enabled on page load
              // (with full block context), never let an AJAX render (which may
              // lack overlay-group block access) revert them. Without this, an
              // AJAX cart-drawer render that lost the upsell/progress/gift block
              // context would silently disable the matching enforcement on the
              // client and the feature would vanish until a full reload.
              if (attr === 'data-cfg-gift' && drawer.getAttribute('data-cfg-gift') === 'true') return;
              if (attr === 'data-cfg-progress' && drawer.getAttribute('data-cfg-progress') === 'true') return;
              if (attr === 'data-cfg-upsell' && drawer.getAttribute('data-cfg-upsell') === 'true') return;
              drawer.setAttribute(attr, val);
            }
          });
        }

        const sourceInner = doc.querySelector('#CartDrawer .drawer__inner');
        const targetInner = document.querySelector('#CartDrawer .drawer__inner');
        if (sourceInner && targetInner) {
          targetInner.innerHTML = sourceInner.innerHTML;
        } else {
          const sectionElement = document.querySelector(section.fallbackSelector || '#CartDrawer');
          if (sectionElement) {
            sectionElement.innerHTML = this.getSectionInnerHTML(raw, section.fallbackSelector || '#CartDrawer');
          }
        }
      } else {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);

        if (!sectionElement) return;
        sectionElement.innerHTML = this.getSectionInnerHTML(raw, section.selector);
      }
    });

    _lastDrawerUpdate = Date.now();
    mergeLiveProgressNode(liveProgressNode);
    if (typeof enforceDrawerConfig === 'function') {
      enforceDrawerConfig();
    }
    /* Keep the upsell DOM node alive across empty-cart states. CSS in
       component-cart-drawer.css (cart-drawer-items.is-empty /
       cart-drawer.is-empty) hides everything in .cart-drawer__footer except
       the gift selector, so a hidden upsell still becomes visible the
       moment the next add lands and the is-empty class is removed — no
       re-fetch race, no missing-after-re-add bug. */
    if (typeof mergeLiveUpsellNode === 'function') {
      mergeLiveUpsellNode(liveUpsell);
    }
    if (typeof scheduleEnforceDrawerConfig === 'function') {
      scheduleEnforceDrawerConfig();
    } else if (typeof enforceDrawerConfig === 'function') {
      enforceDrawerConfig();
    }

    /* Defensive restore: if the freshly-rendered drawer somehow lost the gift
       selector but config says it must be present, drop the snapshot back in
       place (inside .cart-drawer__footer, right above .cart-drawer__order-summary
       so it sits directly on top of the estimated total row). Then async-
       refresh from `?section_id=cart-drawer` so the restored copy reflects the
       latest tier/claim state. */
    if (giftConfigEnabled && giftSnapshot && !document.querySelector('#CartDrawer cart-drawer-gift-selector')) {
      const liveInner = document.querySelector('#CartDrawer .drawer__inner');
      if (liveInner) {
        const innerFooter = liveInner.querySelector('.cart-drawer__footer');
        if (innerFooter) {
          const orderSummary = innerFooter.querySelector('.cart-drawer__order-summary');
          if (orderSummary) {
            innerFooter.insertBefore(giftSnapshot, orderSummary);
          } else {
            innerFooter.insertBefore(giftSnapshot, innerFooter.firstChild);
          }
        } else {
          liveInner.appendChild(giftSnapshot);
        }
      }
    }

    /* Same defensive restore for the auto-mode controller. The element is
       hidden / aria-hidden but its connectedCallback wires the cart:updated
       + pub-sub listeners that drive the auto-add. Losing it silently kills
       all subsequent gift reconciles. */
    if (giftConfigEnabled && autoGiftSnapshot && !document.querySelector('#CartDrawer cart-drawer-auto-gift')) {
      const liveInner = document.querySelector('#CartDrawer .drawer__inner');
      if (liveInner) {
        const innerFooter = liveInner.querySelector('.cart-drawer__footer');
        const target = innerFooter || liveInner;
        target.appendChild(autoGiftSnapshot);
      }
    }

    /* Always force a fresh gift-selector fetch after any cart re-render when
       the gift feature is enabled. This guarantees tier slides reflect the
       current cart total (so crossing a threshold unlocks the tier visually
       without needing a page refresh). The returned Promise settles AFTER
       the replaceWith DOM swap lands, so we can chain a follow-up
       cart:updated that the newly-mounted selector will actually observe. */
    const giftRefreshP = (giftConfigEnabled && typeof refreshGiftSelectorFromSection === 'function')
      ? (refreshGiftSelectorFromSection() || Promise.resolve())
      : Promise.resolve();

    this.querySelectorAll('cart-drawer-gift-selector').forEach((el) => {
      if (typeof el._initUI === 'function') el._initUI();
      if (el.dataset.useCarousel === 'true' && typeof window.initGiftTierCarousel === 'function') {
        window.initGiftTierCarousel(el);
      }
    });

    document.dispatchEvent(new CustomEvent('cart:updated', {
      detail: { source: 'renderContents', cartData: parsedState },
    }));

    // After the async gift refresh lands, fire a SECOND cart:updated so the
    // newly-mounted gift selector (replaceWith'd inside
    // refreshGiftSelectorFromSection) reconciles against the authoritative
    // cart total. Without this, the replacement node mounts AFTER the
    // synchronous dispatch above and never sees the event.
    Promise.resolve(giftRefreshP).then(() => {
      document.dispatchEvent(new CustomEvent('cart:updated', {
        detail: { source: 'renderContents:post-gift-refresh', cartData: parsedState },
      }));
    }).catch((err) => { if (window.DEBUG_CART) console.warn('[cart] renderContents: post-gift-refresh dispatch failed', err); });

    animateProgressTransition(prevProgress);

    if (typeof enforceGiftThreshold === 'function' && typeof routes !== 'undefined') {
      fetch(`${routes.cart_url}.js`)
        .then((r) => r.json())
        .then((cartData) => enforceGiftThreshold(cartData))
        .catch((err) => { if (window.DEBUG_CART) console.warn('[cart] renderContents: enforceGiftThreshold fetch failed', err); });
    }

    requestAnimationFrame(() => {
      if (this.boundClose) {
        this.querySelector('#CartDrawer-Overlay')?.removeEventListener('click', this.boundClose);
      }
      this.boundClose = this.close.bind(this);
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.boundClose);
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    return el ? el.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer .drawer__inner',
        fallbackSelector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  connectedCallback() {
    super.connectedCallback();
    this.onSizePickerClick = this.onSizePickerClick.bind(this);
    this.addEventListener('click', this.onSizePickerClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onSizePickerClick);
    super.disconnectedCallback();
  }

  onSizePickerClick(event) {
    const btn = event.target.closest('.cart-drawer__size-picker-btn');
    if (!btn || btn.disabled || btn.classList.contains('cart-drawer__size-picker-btn--current')) return;
    const picker = btn.closest('.cart-drawer__size-picker');
    if (!picker) return;
    event.preventDefault();
    const line = parseInt(picker.dataset.line, 10);
    const quantity = parseInt(picker.dataset.quantity, 10);
    const newVariantId = btn.dataset.variantId;
    const rollbackVariantId = picker.dataset.currentVariantId;
    const sellingPlan = picker.dataset.sellingPlan || '';
    const propsEl = document.getElementById(`CartDrawer-LineProps-${line}`);
    let properties = {};
    if (propsEl) {
      try {
        properties = JSON.parse(propsEl.textContent);
      } catch (e) {
        properties = {};
      }
    }
    this.changeLineItemVariant(line, newVariantId, quantity, sellingPlan, properties, rollbackVariantId);
  }

  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
